import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchFromApi } from '../apiClient';
import { PaginationQueryParams } from 'types/queryTypes';
import { SortOrder } from 'hooks/useTableControls';
import {
  ControlDocument,
  CreateControlDocumentPayload,
  EditedControlDocumentItem,
} from 'types/controlDocumentsTypes';

const CONTROL_DOCUMENT_QUERY_KEY = 'control-documents';
const CONTROL_DOCUMENT_BY_ID_QUERY = 'control-document-by-id';
const REACT_APP_DARWIN_URL = process.env.REACT_APP_DARWIN_URL;

/**
 * Get all control documents by company
 */
export const useGetControlDocuments = ({
  companyId,
  page = 1,
  limit = 10,
  query = '',
  sortOrder = { sortBy: 'id', order: 'ASC' },
}: Partial<PaginationQueryParams> & { companyId: string } & {
  sortOrder: SortOrder;
}) =>
  useQuery({
    queryKey: [CONTROL_DOCUMENT_QUERY_KEY, limit, page, query, sortOrder],
    queryFn: () =>
      fetchFromApi<ControlDocument[]>(
        `${REACT_APP_DARWIN_URL}/control-documents/company/${companyId}?limit=${limit}&page=${page}&search=${query}&order=${sortOrder.order}&sortBy=${sortOrder.sortBy}`
      ),
    enabled: !!companyId,
    refetchOnWindowFocus: false,
  });

/**
 * Create a control document
 */
export const useCreateControlDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      companyId,
    }: {
      payload: CreateControlDocumentPayload;
      companyId: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/control-documents/company/${companyId}`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONTROL_DOCUMENT_QUERY_KEY] });
    },
  });
};

/**
 * Get control document by id
 */
export const useGetControlDocumentById = (
  companyId: string,
  controlDocumentId: string
) => {
  return useQuery({
    queryKey: [CONTROL_DOCUMENT_BY_ID_QUERY, companyId, controlDocumentId],
    queryFn: () =>
      fetchFromApi<ControlDocument>(
        `${REACT_APP_DARWIN_URL}/control-documents/${companyId}/${controlDocumentId}`
      ),
    enabled: !!companyId && !!controlDocumentId,
    refetchOnWindowFocus: false,
  });
};

/**
 * Edit a control document
 */
export const useEditControlDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      companyId,
      controlDocumentId,
    }: {
      payload: CreateControlDocumentPayload;
      companyId: string | number;
      controlDocumentId: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/control-documents/${controlDocumentId}/company/${companyId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONTROL_DOCUMENT_QUERY_KEY] });
    },
  });
};

/**
 * Delete a control document
 */
export const useDeleteControlDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      controlDocumentId,
    }: {
      companyId: string | number;
      controlDocumentId: string;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/control-documents/${controlDocumentId}/company/${companyId}`,
        {
          method: 'DELETE',
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONTROL_DOCUMENT_QUERY_KEY] });
    },
  });
};

/**
 * Edit a control document status
 */
export const useEditControlDocumentStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      companyId,
      controlDocumentId,
    }: {
      payload: { isActive: boolean };
      companyId: string | number;
      controlDocumentId: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/control-documents/${controlDocumentId}/company/${companyId}/active`,
        {
          method: 'PATCH',
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONTROL_DOCUMENT_QUERY_KEY] });
    },
  });
};

/**
 * Bulk edit multiple control documents' aliases/status
 */
export const useBulkEditControlDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      companyId,
    }: {
      payload: { controlDocuments: EditedControlDocumentItem[] };
      companyId: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/control-documents/company/${companyId}/bulk-update`,
        {
          method: 'PATCH',
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONTROL_DOCUMENT_QUERY_KEY] });
    },
  });
};
